import {
  getV11DashboardsEnv,
  useDeleteV11DashboardsEnv,
  usePostV11DashboardsEnv,
} from '@/api-client/dashboard/queries/dashboard-env-controller/dashboard-env-controller';
import { UserEnvValue } from '@/api-client/dashboard/model';
import {
  getEnvValueByPatternFromMap,
  getEnvValueFromMap,
  getEnvValuesFromMap,
  mergeUserEnvDataIntoMap,
  userEnvDataToMap,
} from '@/common/utils/userEnv.utils';
import { createDevOnlyKeyTracker } from '@/common/utils/userEnvDev.utils';
import { defineStore } from 'pinia';
import { ref, shallowRef } from 'vue';

export const useDashboardUserEnvStore = defineStore('useDashboardUserEnvStore', () => {
  const dashboardId = ref<number | null>(null);
  const dashboardUserEnvMap = shallowRef<Map<string, string>>(new Map());
  const hasUserEnvInfo = ref(false);

  const setDashboardId = (id: number | null) => {
    dashboardId.value = id;
  };

  const fetchAllEnvData = (data?: UserEnvValue[]) => {
    hasUserEnvInfo.value = true;
    dashboardUserEnvMap.value = userEnvDataToMap(data);
  };

  const addNewEnvData = (data?: UserEnvValue[]) => {
    dashboardUserEnvMap.value = mergeUserEnvDataIntoMap(dashboardUserEnvMap.value, data);
  };

  const fetchEnvDataByKeys = async (keys: string[], global = false) => {
    const id = dashboardId.value;
    if (id == null) {
      console.error('fetchEnvDataByKeys: dashboardId is null');
      return;
    }

    try {
      const { data } = await getV11DashboardsEnv({ dashboardId: id, keys, global });
      if (data?.data?.length) {
        dashboardUserEnvMap.value = mergeUserEnvDataIntoMap(dashboardUserEnvMap.value, data.data);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const { mutateAsync: deleteDashboardEnv } = useDeleteV11DashboardsEnv();

  /**
   * @param key 삭제할 유저 환경 변수 키
   * @param all 모든 유저 환경 변수 삭제 여부
   * @param global global/ custom table 구분
   * @returns 삭제 성공 여부
   */
  const clearUserEnvValue = async (key: string, all = false, global = false) => {
    const id = dashboardId.value;
    if (id == null) {
      console.error('clearUserEnvValue: dashboardId is null');
      return false;
    }

    try {
      await deleteDashboardEnv({
        data: { dashboardId: id, keys: [key], allUserEnvDelete: all, global },
      });

      const next = new Map(dashboardUserEnvMap.value);
      next.delete(key);
      dashboardUserEnvMap.value = next;
      return true;
    } catch (e) {
      console.log(e);
      return false;
    }
  };

  const devOnly = createDevOnlyKeyTracker(clearUserEnvValue);

  const { mutateAsync: setDashboardEnv } = usePostV11DashboardsEnv();

  const setEnvValue = async <T extends Record<string, any> | string>(
    newEnv: { key: string; value: T }[],
    global = false,
  ) => {
    const id = dashboardId.value;
    if (id == null) {
      console.error('setEnvValue: dashboardId is null');
      return;
    }

    const convertedValue = newEnv.map(({ key, value }) => ({
      dashboardId: id,
      key,
      value: typeof value === 'string' ? value : JSON.stringify(value),
    }));
    try {
      await setDashboardEnv({ data: convertedValue, params: { global } });
      const next = new Map(dashboardUserEnvMap.value);
      convertedValue.forEach(({ key, value }) => {
        next.set(key, value);
        devOnly.trackKey(key);
      });
      dashboardUserEnvMap.value = next;
    } catch (e) {
      console.error(e);
    }
  };

  const getEnvValue = <T>(key?: string): T | null => {
    return getEnvValueFromMap<T>(dashboardUserEnvMap.value, key);
  };

  const getEnvValues = (keys: string[]): { [key in string]: unknown | null }[] => {
    return getEnvValuesFromMap(dashboardUserEnvMap.value, keys) as {
      [key in string]: unknown | null;
    }[];
  };

  const getEnvValueByPattern = (word: string): { [key in string]: unknown | null } => {
    return getEnvValueByPatternFromMap(dashboardUserEnvMap.value, word) as {
      [key in string]: unknown | null;
    };
  };

  const initUserEnvMap = () => {
    hasUserEnvInfo.value = false;
    dashboardUserEnvMap.value = new Map();
    devOnly.clearAll();
  };

  return {
    dashboardId,
    setDashboardId,
    dashboardUserEnvMap,
    hasUserEnvInfo,
    fetchAllEnvData,
    addNewEnvData,
    fetchEnvDataByKeys,
    clearUserEnvValue,
    setEnvValue,
    getEnvValue,
    getEnvValues,
    getEnvValueByPattern,
    initUserEnvMap,
    ...devOnly,
  };
});
